/** Typed wrappers per service. The path prefixes match the Caddy routes. */

import type { ApiClient } from "./client";
import type {
  AgentRecord,
  AgentStatusResponse,
  AnalyticsResponse,
  CallHistoryResponse,
  CallRecord,
  ChannelsResponse,
  CallTypeRecord,
  ClientCard,
  ClientRecord,
  InstructionRecord,
  DidRecord,
  FormFieldRecord,
  QueueRecord,
  HoldMusicState,
  HoldMusicUploadResult,
  MeResponse,
  MonitorMode,
  MonitorStartResponse,
  PermissionsResponse,
  PlatformUser,
  RecordingUrlResponse,
  RetentionSetting,
  TranscriptResponse,
  SipCredentials,
  TokenResponse,
} from "./types";

export const identity = (api: ApiClient) => ({
  // The credential endpoints use postPublic: a stale bearer attached to the
  // request that exists to replace it is how an expiry became a lockout.
  login: (username: string, password: string) =>
    api.postPublic<TokenResponse>("/api/identity/auth/login", { username, password }),

  /** Refresh tokens ROTATE: the presented one is revoked server-side. */
  refresh: (refreshToken: string) =>
    api.postPublic<TokenResponse>("/api/identity/auth/refresh", { refresh_token: refreshToken }),

  logout: (refreshToken: string) =>
    api.postPublic<void>("/api/identity/auth/logout", { refresh_token: refreshToken }),

  permissions: () => api.get<PermissionsResponse>("/api/identity/connect/permissions"),
});

export const callctl = (api: ApiClient) => ({
  listCalls: () => api.get<ChannelsResponse>("/api/callctl/calls"),

  hold: (uuid: string) => api.post<void>(`/api/callctl/calls/${uuid}/hold`),
  unhold: (uuid: string) => api.post<void>(`/api/callctl/calls/${uuid}/unhold`),
  hangup: (uuid: string) => api.post<void>(`/api/callctl/calls/${uuid}/hangup`),

  blindTransfer: (uuid: string, to: string) =>
    api.post<void>(`/api/callctl/calls/${uuid}/transfer`, { to }),

  attendedStart: (uuid: string, target: string) =>
    api.post<void>(`/api/callctl/calls/${uuid}/attended`, { target }),
  /**
   * `released` = the caller's carrier took the whole call over (REFER honored);
   * `bridged` = the fallback — caller↔client stay bridged on our switch.
   */
  attendedComplete: (uuid: string) =>
    api.post<{ mode: "released" | "bridged" }>(`/api/callctl/calls/${uuid}/attended/complete`),
  attendedCancel: (uuid: string) => api.post<void>(`/api/callctl/calls/${uuid}/attended/cancel`),

  /** Starts a NEW monitor leg. To change mode on a running one, use setMonitorMode. */
  monitorStart: (uuid: string, supervisorExt: string, mode: MonitorMode) =>
    api.post<MonitorStartResponse>(`/api/callctl/monitor/${uuid}`, {
      supervisor_ext: supervisorExt,
      mode,
    }),

  setMonitorMode: (uuid: string, mode: MonitorMode) =>
    api.put<{ mode: MonitorMode }>(`/api/callctl/monitor/${uuid}/mode`, { mode }),
});

export const orchestrator = (api: ApiClient) => ({
  agentStatus: () => api.get<AgentStatusResponse>("/api/orchestrator/agents/status"),

  setAgentStatus: (id: string, status: string) =>
    api.put<{ extension: string; status: string }>(`/api/orchestrator/agents/${id}/status`, {
      status,
    }),

  // The agent app's self surface. 404 means "no agent is linked to your user"
  // — a configuration state the app renders, not an error to retry.
  me: () => api.get<MeResponse>("/api/orchestrator/agents/me"),

  /** The agent's own SIP secret — everything the softphone needs to register. */
  myCredentials: () => api.get<SipCredentials>("/api/orchestrator/agents/me/credentials"),

  setMyStatus: (status: string) =>
    api.put<{ extension: string; status: string }>("/api/orchestrator/agents/me/status", {
      status,
    }),

  // End the wrap-up window early ("I'm ready"). ended:false = there was no
  // active window (it expired mid-flight, or someone else already ended it).
  endMyWrapUp: () => api.post<{ ended: boolean }>("/api/orchestrator/agents/me/wrap-up/end", {}),

  endAgentWrapUp: (id: string) =>
    api.post<{ ended: boolean }>(`/api/orchestrator/agents/${id}/wrap-up/end`, {}),

  // Admin provisioning records (telephony:agents.manage).
  listAgents: () => api.get<AgentRecord[]>("/api/orchestrator/agents"),

  /** PATCH one agent; identity_user_id: "" clears the app-user link. The
   * timing knobs (all seconds / counts) reconcile into the switch on save. */
  updateAgent: (
    id: string,
    patch: Partial<
      Pick<
        AgentRecord,
        "display_name" | "enabled" | "identity_user_id" | "ring_seconds" | "max_no_answer" | "wrap_up_time"
      >
    >,
  ) => api.patch<AgentRecord>(`/api/orchestrator/agents/${id}`, patch),

  // Hold music (telephony:settings.manage): what a parked caller hears during
  // a consult. One file, replaced wholesale on upload; delete = ringback.
  // Clients & DIDs (BACKLOG §12). The card is readable by whoever answers
  // (clients.view); the rest needs clients.manage.
  clientCard: (id: string) => api.get<ClientCard>(`/api/orchestrator/clients/${id}/card`),
  listClients: () => api.get<ClientRecord[]>("/api/orchestrator/clients"),
  createClient: (body: { name: string; greeting?: string; queue_id?: string }) =>
    api.post<ClientRecord>("/api/orchestrator/clients", body),
  updateClient: (id: string, patch: Partial<Pick<ClientRecord, "name" | "greeting" | "queue_id" | "enabled">>) =>
    api.patch<ClientRecord>(`/api/orchestrator/clients/${id}`, patch),
  deleteClient: (id: string) => api.delete<void>(`/api/orchestrator/clients/${id}`),
  listClientDids: (id: string) => api.get<DidRecord[]>(`/api/orchestrator/clients/${id}/dids`),
  addClientDid: (id: string, number: string) =>
    api.post<DidRecord>(`/api/orchestrator/clients/${id}/dids`, { number }),
  removeDid: (didId: string) => api.delete<void>(`/api/orchestrator/dids/${didId}`),

  // Call types and their ordered instructions (BACKLOG §12 slice 2).
  listCallTypes: (clientId: string) =>
    api.get<CallTypeRecord[]>(`/api/orchestrator/clients/${clientId}/call-types`),
  createCallType: (clientId: string, name: string) =>
    api.post<CallTypeRecord>(`/api/orchestrator/clients/${clientId}/call-types`, { name }),
  // `fields` replaces the WHOLE form: the builder edits a document, so there
  // is no partial-field patch to express. Omit it to leave the form alone.
  updateCallType: (
    id: string,
    patch: { name?: string; enabled?: boolean; fields?: FormFieldRecord[] },
  ) =>
    api.patch<CallTypeRecord>(`/api/orchestrator/call-types/${id}`, patch),
  deleteCallType: (id: string) => api.delete<void>(`/api/orchestrator/call-types/${id}`),
  // Reorder takes the FULL list, like queue members — a partial order is meaningless.
  reorderCallTypes: (clientId: string, ids: string[]) =>
    api.put<void>(`/api/orchestrator/clients/${clientId}/call-types/order`, { ids }),

  listInstructions: (callTypeId: string) =>
    api.get<InstructionRecord[]>(`/api/orchestrator/call-types/${callTypeId}/instructions`),
  addInstruction: (callTypeId: string, body: string) =>
    api.post<InstructionRecord>(`/api/orchestrator/call-types/${callTypeId}/instructions`, { body }),
  updateInstruction: (id: string, body: string) =>
    api.patch<InstructionRecord>(`/api/orchestrator/instructions/${id}`, { body }),
  deleteInstruction: (id: string) => api.delete<void>(`/api/orchestrator/instructions/${id}`),
  reorderInstructions: (callTypeId: string, ids: string[]) =>
    api.put<void>(`/api/orchestrator/call-types/${callTypeId}/instructions/order`, { ids }),

  /** Classify a LIVE call. Sets channel variables that land in the CDR at
   * hangup — there is no CDR row to update mid-call. Re-picking overwrites. */
  setCallType: (callUuid: string, callTypeId: string) =>
    api.post<{ call_uuid: string; call_type_id: string; call_type_name: string }>(
      `/api/orchestrator/calls/${callUuid}/call-type`,
      { call_type_id: callTypeId },
    ),

  listQueues: () => api.get<QueueRecord[]>("/api/orchestrator/queues"),
  updateQueue: (id: string, patch: Partial<Pick<QueueRecord, "name" | "strategy" | "enabled" | "is_default">>) =>
    api.patch<QueueRecord>(`/api/orchestrator/queues/${id}`, patch),

  holdMusic: () => api.get<HoldMusicState>("/api/orchestrator/telephony/hold-music"),
  uploadHoldMusic: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.putForm<HoldMusicUploadResult>("/api/orchestrator/telephony/hold-music", form);
  },
  deleteHoldMusic: () => api.delete<void>("/api/orchestrator/telephony/hold-music"),
});

/**
 * Identity's admin surface — only what the supervisor app's Admin view needs.
 * A 403 here means the signed-in user is a telephony admin without identity
 * admin rights; callers degrade to read-only rather than treating it as an
 * error.
 */
export const identityAdmin = (api: ApiClient) => ({
  listUsers: () => api.get<PlatformUser[]>("/api/identity/admin/users"),
});

export interface HistoryQuery {
  from?: string;
  to?: string;
  direction?: string;
  agent?: string;
  queue?: string;
  client_id?: string;
  call_type_id?: string;
  number?: string;
  disposition?: string[];
  limit?: number;
  cursor?: string;
}

export const cdr = (api: ApiClient) => ({
  history: (q: HistoryQuery = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) {
        for (const item of v) params.append(k, String(item));
      } else {
        params.set(k, String(v));
      }
    }
    const qs = params.toString();
    return api.get<CallHistoryResponse>(`/api/cdr/calls${qs ? `?${qs}` : ""}`);
  },

  call: (uuid: string) => api.get<CallRecord>(`/api/cdr/calls/${uuid}`),

  /** Rolled up server-side from the continuous aggregate — see cdr.calls_15m. */
  analytics: (q: { from?: string; to?: string; bucket?: "15m" | "1h" | "1d"; queue?: string; direction?: string } = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v) params.set(k, v);
    }
    const qs = params.toString();
    return api.get<AnalyticsResponse>(`/api/cdr/analytics${qs ? `?${qs}` : ""}`);
  },

  /** 404 = no transcript row (not recorded, or not yet uploaded). */
  transcript: (uuid: string) => api.get<TranscriptResponse>(`/api/cdr/calls/${uuid}/transcript`),

  // Retention is ADMIN policy (settings.manage): the value set here wins over
  // the install's env default and applies on the reaper's next pass.
  recordingRetention: () => api.get<RetentionSetting>("/api/cdr/settings/recording-retention"),
  setRecordingRetention: (days: number) =>
    api.put<RetentionSetting>("/api/cdr/settings/recording-retention", { days }),

  /**
   * Mints a short-lived playback grant. The returned URL carries its own
   * signature, because an <audio> element cannot send an Authorization header
   * and re-requests with a Range on every seek.
   */
  recordingUrl: (uuid: string, download = false) =>
    api.get<RecordingUrlResponse>(
      `/api/cdr/calls/${uuid}/recording-url${download ? "?download=1" : ""}`,
    ),
});
